import styled from '@emotion/styled'
import { checkAmountRemaining, formatDollars } from '@open-tender/utils'
import {
  selectBrand,
  selectCheckout,
  selectOrder,
  selectRecaptcha,
  setRecaptchaToken,
  setSubmitting,
  submitOrder,
  updateForm,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import { Button, ButtonLink, Message } from 'components'
import CheckoutSection from './CheckoutSection'
import FreedomPay from '../../FreedomPay'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useEffect, useRef, useState } from 'react'
import {
  CheckoutConfigFieldAddress,
  CheckoutConfigFieldDetails,
  CheckoutFormAddress,
  CheckoutTenderCreditCustomer,
  OrderCreateDetails,
} from '@open-tender/types'
import { CvvRegex } from './Checkout.utils'

const DUPLICATE_ORDER_ERROR =
  'kindly wait for a min & check order history before submitting again to avoid duplicate orders'
const DUPLICATE_ORDER_COOLDOWN_SECONDS = 60

const CheckoutSubmitButton = styled.div`
  display: flex;
  justify-content: flex-start;
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    justify-content: center;
  }
`

const CheckoutSubmitMessage = styled.div`
  margin: 3rem 0 0;

  > div {
    margin: 0 0 3rem;
  }
`

export const FreedomPayContainer = styled(FreedomPay)`
  margin-top: 3.2rem;
`

const CheckoutSubmitSave = styled.div`
  margin: 3rem 0 0;
`

const hasMissingDetails = (
  required: CheckoutConfigFieldDetails[],
  details: OrderCreateDetails
) => {
  const missingNotes = required.includes('notes') && !details.notes
  const missingPersonCount = required.includes('count') && !details.person_count

  return missingNotes || missingPersonCount
}

const hasMissingAddress = (
  required: CheckoutConfigFieldAddress[],
  address: CheckoutFormAddress
) => {
  const missingCompany = required.includes('company') && !address.company
  const missingContact = required.includes('contact') && !address.contact
  const missingPhone = required.includes('phone') && !address.phone
  const missingUnit = required.includes('unit') && !address.unit

  return missingCompany || missingContact || missingPhone || missingUnit
}

const CheckoutSubmit = ({ paymentLink }: { paymentLink?: boolean }) => {
  const dispatch = useAppDispatch()
  const { orderType, serviceType } = useAppSelector(selectOrder)
  const brandConfig = useAppSelector(selectBrand)
  const { require_cardholder_name = false, use_enterprise_recaptcha } =
    brandConfig || {}
  const isSubmittingBlocked = useRef(false)
  const { check, form, submitting, loading, errors } =
    useAppSelector(selectCheckout)
  const { addCard: includeRecaptcha } = useAppSelector(selectRecaptcha) || {
    addCard: false,
  }
  const requiredDetails = check?.config.required.details || []
  const requiredAddress = check?.config.required.address || []
  const updating = submitting ? false : loading === 'pending'
  const total = check?.totals.total
  const deposit = check?.deposit
  const amountDue = deposit || total
  const amountRemaining = amountDue
    ? checkAmountRemaining(amountDue, form.tenders)
    : 0.0
  const isPaid = Math.abs(amountRemaining).toFixed(2) === '0.00'
  const missingDetails = hasMissingDetails(requiredDetails, form.details)
  const isDelivery = serviceType === 'DELIVERY'
  const missingAddress =
    isDelivery && hasMissingAddress(requiredAddress, form.address)

  const require_cvv_for_saved_cards: boolean = brandConfig?.require_cvv_for_saved_cards || false
  const creditCard = form.tenders.find((i) => i.tender_type === 'CREDIT') as CheckoutTenderCreditCustomer | undefined
  const cvvValid =
    !require_cvv_for_saved_cards ||
    !creditCard ||
    (!!creditCard.cvv && CvvRegex.test(creditCard.cvv))

  const [submitDisabled, setSubmitDisabled] = useState(false)

  const [cooldownSeconds, setCooldownSeconds] = useState(0)
  const isDuplicateOrderError =
    typeof errors?.form === 'string' &&
    errors.form.toLowerCase().includes(DUPLICATE_ORDER_ERROR)
  const cooldownStartedRef = useRef(false)

  useEffect(() => {
    if (isDuplicateOrderError) {
      if (!cooldownStartedRef.current) {
        cooldownStartedRef.current = true
        setCooldownSeconds(DUPLICATE_ORDER_COOLDOWN_SECONDS)
      }
    } else {
      cooldownStartedRef.current = false
    }
  }, [isDuplicateOrderError])

  useEffect(() => {
    if (cooldownSeconds <= 0) return
    const timer = setTimeout(() => setCooldownSeconds((s) => s - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldownSeconds])

  useEffect(() => {
    setSubmitDisabled(
      isSubmittingBlocked.current ||
      submitting ||
      !isPaid ||
      updating ||
      (includeRecaptcha && !use_enterprise_recaptcha && !form.token) ||
      missingDetails ||
      missingAddress ||
      !cvvValid ||
      cooldownSeconds > 0
    )
  }, [submitting, updating, isPaid, includeRecaptcha, use_enterprise_recaptcha, form.token, missingDetails, missingAddress, cvvValid, cooldownSeconds])

  const hasTotal = !!(amountDue && parseFloat(amountDue) >= 0)
  const totalAmount = hasTotal ? ` ${formatDollars(amountDue ?? '0')}` : ''
  const { executeRecaptcha } = useGoogleReCaptcha()

  const submitPayment = async () => {
    if (isSubmittingBlocked.current) return
    isSubmittingBlocked.current = true
    const mlSessionId = window.localStorage.getItem('ml_session_id')
    if (mlSessionId) dispatch(updateForm({ session_id: mlSessionId }))
    if (includeRecaptcha) {
      const token = await executeRecaptcha?.('addCard')
      dispatch(setRecaptchaToken(token))
    }
    dispatch(setSubmitting(true))
    await dispatch(submitOrder())
    isSubmittingBlocked.current = false
  }

  const saveForLater = () => {
    const mlSessionId = window.localStorage.getItem('ml_session_id')
    if (mlSessionId) dispatch(updateForm({ session_id: mlSessionId }))
    dispatch(setSubmitting(true))
    dispatch(submitOrder({ save: true }))
  }

  return (
    <CheckoutSection>
      <CheckoutSubmitMessage>
        {!isPaid ? (
          <Message
            as="div"
            size="small"
            color="alert"
            style={{ width: '100%', padding: '1rem 1.5rem' }}
          >
            There is a balance of ${amountRemaining.toFixed(2)} remaining on
            your order. Please add a payment above.
          </Message>
        ) : null}
      </CheckoutSubmitMessage>
      <CheckoutSubmitButton>
        <Button
          onClick={submitPayment}
          disabled={submitDisabled}
          size="big"
          color="primary"
        >
          {updating ? 'Updating...' : `Submit Order${totalAmount}`}
        </Button>
      </CheckoutSubmitButton>
      {!paymentLink && orderType === 'CATERING' ? (
        <CheckoutSubmitSave>
          <ButtonLink onClick={saveForLater}>
            Or save this order for later (your payment method will not be
            charged).
          </ButtonLink>
        </CheckoutSubmitSave>
      ) : null}
      {require_cardholder_name && <FreedomPayContainer />}
    </CheckoutSection>
  )
}

export default CheckoutSubmit
